<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { useFetchTransactions } from '@/hooks/use-transactions';
import { useTransactionStore } from '@/store/use-transaction-store';
import { type Transaction } from '@/types/transaction';
import { formatDate } from '@/utils/utils';
import { ChevronLeft, ChevronRight, Download } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';


// Get store
const transactionStore = useTransactionStore()

// Get reactive reference to transactionType from store
const { transactionType } = storeToRefs(transactionStore)

// Fetch transactions with transactionType from store
const { data: transactions, isLoading, isError, error } = useFetchTransactions(transactionType.value)

// Pagination
const currentPage = ref(1)
const itemsPerPage = ref(15)

const totalPages = computed(() => 
  Math.ceil((transactions.value?.length || 0) / itemsPerPage.value)
)

const paginatedData = computed(() => {
  if (!transactions.value) return []
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return transactions.value.slice(start, end)
})

const goToPage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

// TODO: Think move this to the Backend. CSV Download
const downloadCSV = () => {
  if (!transactions.value || transactions.value.length === 0) {
    alert('No data to download')
    return
  }

  // Define CSV headers
  const headers = [
    'Membership Number',
    'Scheme Code',
    'Option Code',
    'Option Name',
    'Dependent Code',
    'Names',
    'Surname',
    'Date Enrolled',
    'Transaction Date',
    'Status',
    'Description'
  ]

  // Extract data rows
  const rows = transactions.value.map((tx: Transaction) => [
    tx?.meta_data?.membership_number,
    tx?.meta_data?.scheme_code,
    tx?.meta_data?.option_code,
    tx?.meta_data?.option_name,
    tx?.meta_data?.dependant_code,
    tx?.meta_data?.health_seeker?.first_name,
    tx?.meta_data?.health_seeker?.surname,
    tx?.meta_data?.date_enrolled,
    formatDate(tx?.created),
    tx?.status,
    tx?.status_description
  ])

  // Create CSV content
  const csvContent = [
    headers.join(','),
    ...rows.map(row => 
      row.map(cell => {
        // Escape cells that contain commas or quotes
        if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"'))) {
          return `"${cell.replace(/"/g, '""')}"`
        }
        return cell
      }).join(',')
    )
  ].join('\n')

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  
  link.setAttribute('href', url)
  link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.csv`)
  link.style.visibility = 'hidden'
  
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

</script>

<template>
  <div class="p-0">
    <!-- Header -->
    <div class="flex items-center justify-between border-b border-gray-200 pb-6 mb-6">
      <div>
        <h2 class="text-2xl font-semibold text-gray-800">
          {{ transactionType?.replace(/_/g, ' ').toUpperCase() }} Transactions
          <span v-if="transactions?.length > 0" class="text-slate-500">
            ({{ transactions.length }})
          </span>
        </h2>
      </div>
      
      <Button
        @click="downloadCSV"
        :disabled="!transactions || transactions.length === 0 || isLoading"
        class="bg-[#255f7a] text-white hover:bg-[#225f7b]"
      >
        <Download class="mr-2 h-4 w-4" />
        Download Report
      </Button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex flex-col items-center justify-center py-20">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mb-4"></div>
      <p class="text-lg text-gray-600 font-medium">Loading transactions...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="isError" class="flex flex-col items-center justify-center py-20">
      <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
        <h3 class="text-lg font-semibold text-red-900 mb-3">Error Loading Data</h3>
        <p class="text-red-700 text-sm">{{ error?.message || 'Failed to load transactions' }}</p>
      </div>
    </div>

    <!-- Table -->
    <div v-else-if="transactions && transactions.length > 0" class="space-y-4">
      <div class="border rounded-lg overflow-hidden bg-white shadow-sm">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50 border-b">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Membership Number
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Scheme
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Option Code
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Option Name
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Dep Code
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Names
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Surname
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Date Enrolled
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Trans Date
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Trans Type
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Status
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Status Description
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr
                v-for="transaction in paginatedData"
                :key="transaction?.id"
                class="hover:bg-gray-50 transition-colors"
              >
                <td class="px-4 py-3">
                  <span class="text-sm font-mono text-gray-700">
                    {{ transaction?.meta_data?.membership_number }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-200 text-slate-800">
                    {{ transaction?.meta_data?.scheme_code }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span class="text-sm text-gray-900 font-medium">
                    {{ transaction?.meta_data?.option_code }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span class="text-sm text-gray-900 font-medium">
                    {{ transaction?.meta_data?.option_name }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span class="text-sm text-gray-700">
                    {{ transaction?.meta_data?.dependant_code }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span class="text-sm text-gray-700">
                    {{ transaction?.meta_data?.health_seeker?.first_name }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span class="text-sm text-gray-700">
                    {{ transaction?.meta_data?.health_seeker?.surname }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span class="text-sm text-gray-700">
                    {{ transaction?.meta_data?.date_enrolled }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span class="text-sm text-gray-700">
                    {{ formatDate(transaction?.created) }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span class="text-xs px-2.5 py-0.5 bg-slate-100 rounded-full">{{ transaction?.transaction_type.toUpperCase() }}</span>
                </td>
                <td class="px-4 py-3">
                  <span
                    :class="[
                      'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                      transaction?.status === 'success'
                        ? 'bg-green-100 text-green-800'
                        : transaction?.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    ]"
                  >
                    {{ transaction?.status }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span
                    :class="[
                      'inline-flex whitespace-nowrap items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                      transaction?.status === 'success'
                        ? 'bg-green-100 text-green-800'
                        : transaction?.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    ]"
                  >
                    {{ transaction?.status_description.substring(0, 33  ) + '...' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div class="flex items-center justify-between px-4 py-3 bg-white border rounded-lg">
        <div class="text-sm text-gray-700">
          Showing {{ ((currentPage - 1) * itemsPerPage) + 1 }} to 
          {{ Math.min(currentPage * itemsPerPage, transactions.length) }} of 
          {{ transactions.length }} results
        </div>
        
        <div class="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            @click="goToPage(currentPage - 1)"
            :disabled="currentPage === 1"
          >
            <ChevronLeft class="h-4 w-4" />
            Previous
          </Button>
          
          <div class="flex gap-1">
            <Button
              v-for="page in totalPages"
              :key="page"
              v-show="page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1"
              size="sm"
              :variant="page === currentPage ? 'default' : 'outline'"
              @click="goToPage(page)"
              class="min-w-[40px]"
            >
              {{ page }}
            </Button>
          </div>
          
          <Button
            size="sm"
            variant="outline"
            @click="goToPage(currentPage + 1)"
            :disabled="currentPage === totalPages"
          >
            Next
            <ChevronRight class="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="flex flex-col items-center justify-center py-20">
      <svg class="h-24 w-24 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
      </svg>
      <h3 class="text-lg font-semibold text-gray-700 mb-2">No Transactions Found</h3>
      <p class="text-gray-500 text-sm">There are no transactions to display.</p>
    </div>
  </div>
</template>